import { useCallback, useRef, useState } from 'react';
import immer from 'immer';
import { noop } from 'lodash';
import { readFieldIfReference } from '@frameio/graphql-client';
import type { Account } from '@frameio/graphql-client/generated';
import {
  DeleteConfirmation,
  truncateName,
  useLoadingToast,
} from '@frameio/shared/resources/shared';
import { useTrackUnsafe } from '@frameio/shared/segment/hooks';
import type { TrackingSourceType } from '@frameio/shared/segment/hooks/utils/trackingUtils';
import type { ToastAlertProps } from '@frameio/vapor';
import {
  type DeleteWorkspaceMutationResult,
  useDeleteWorkspaceMutation,
} from '../gql/generated/DeleteWorkspace';
import TEXT from './generated/TEXT/useDeleteWorkspace';

type DeletedWorkspace = NonNullable<
  NonNullable<
    NonNullable<DeleteWorkspaceMutationResult['data']>['deleteWorkspace']
  >['workspace']
>;

export type DeleteSuccessCallback = (workspace: DeletedWorkspace) => void;

type DeleteWorkspacesOpts = {
  accountId: string;
  onSuccess?: DeleteSuccessCallback;
  trackingSource?: TrackingSourceType;
};

export type DeleteWorkspacesReturn = {
  deleteDialog: React.ReactElement<any>;
  deletingWorkspaceIds: Set<string>;
  showDeleteConfirmation: (workspace: DeletedWorkspace) => void;
};

const DEFAULT_OPTS: Omit<DeleteWorkspacesOpts, 'accountId'> = {
  onSuccess: noop,
};

/**
 * The useDeleteWorkspaces hook can be used to delete a workspace including a
 * step where the user must confirm the deletion by typing in the word "delete."
 * A toast is then displayed if the mutation succeeds or fails.
 *
 * @param opts.onSuccess an optional callback fired if the delete succeeds
 * @return the DeleteConfirmation dialog to render and a function for requesting a delete
 */
export function useDeleteWorkspace(
  opts: DeleteWorkspacesOpts
): DeleteWorkspacesReturn {
  const { accountId, trackingSource, onSuccess } = {
    ...DEFAULT_OPTS,
    ...opts,
  };
  const [workspaceToDelete, setWorkspaceToDelete] =
    useState<DeletedWorkspace | null>(null);
  const [deletingWorkspaceIds, setDeletingWorkspaceIds] = useState<Set<string>>(
    new Set()
  );

  /**
   * Want to retain this message so it doesn't change in the dialog while the
   * dialog close animation is playing.
   */
  const confirmationMessage = useRef<React.ReactNode>(null);
  const [deleteWorkspace] = useDeleteWorkspaceMutation();
  const { addToast, updateAndHideToast } = useLoadingToast();
  const track = useTrackUnsafe();

  const handleConfirm = async () => {
    if (!workspaceToDelete) return;
    const toastId = addToast(TEXT.deleteWorkspaceLoading);
    setDeletingWorkspaceIds(
      immer((ids) => {
        ids.add(workspaceToDelete.id);
      })
    );
    try {
      const response = await deleteWorkspace({
        variables: {
          input: {
            id: workspaceToDelete.id,
          },
        },
        optimisticResponse: {
          __typename: 'Mutation',
          deleteWorkspace: {
            __typename: 'DeleteWorkspacePayload',
            workspace: workspaceToDelete,
          },
        },
        update(cache) {
          cache.modify<Account>({
            id: cache.identify({
              __typename: 'Account',
              id: accountId,
            }),
            fields: {
              workspaces(value, { fieldName, isReference, readField }) {
                const workspaces = readFieldIfReference({
                  fieldName,
                  isReference,
                  readField,
                  value,
                });
                if (!workspaces) return undefined;
                return immer(workspaces, (draft) => {
                  draft.nodes = workspaces.nodes.filter(
                    (node) => node.id !== workspaceToDelete.id
                  );
                  draft.totalCount = Math.max(workspaces.totalCount - 1, 0);
                });
              },
            },
          });
        },
      });
      const { name } = workspaceToDelete;
      let title = TEXT.successMessage(name) as string;
      let variant: ToastAlertProps['variant'] = 'success';
      if (response.errors) {
        title = TEXT.errorMessage(name) as string;
        variant = 'error';
      } else if (response.data?.deleteWorkspace?.workspace) {
        onSuccess?.(response.data.deleteWorkspace.workspace);
      }
      updateAndHideToast(toastId, {
        title,
        variant,
      });
      track('workspace-deleted', { source: trackingSource, count: 1 });
    } catch (e) {
      updateAndHideToast(toastId, {
        title: TEXT.genericErrorMessage,
        variant: 'error',
      });
    } finally {
      setDeletingWorkspaceIds(
        immer((ids) => {
          ids.delete(workspaceToDelete.id);
        })
      );
    }
  };

  const deleteDialog = (
    <DeleteConfirmation
      confirmButtonText={TEXT.deleteWorkspaceConfirmButton}
      isShowing={workspaceToDelete !== null}
      message={confirmationMessage.current}
      onConfirm={handleConfirm}
      onRequestClose={() => {
        setWorkspaceToDelete(null);
      }}
      title={TEXT.deleteWorkspaceTitle}
    />
  );

  const { confirmationMessage: confirmationMessageText } = TEXT;

  const showDeleteConfirmation = useCallback(
    (workspace: DeletedWorkspace) => {
      confirmationMessage.current = confirmationMessageText(workspace.name);
      setWorkspaceToDelete(workspace);
    },
    [confirmationMessageText]
  );

  return {
    deleteDialog,
    deletingWorkspaceIds,
    showDeleteConfirmation,
  };
}

TEXT.DEFINE(
  {
    /** Confirmation dialog message for deleting a workspace */
    confirmationMessage: (name: string) => {
      const truncatedName = truncateName(name, 30);
      return (
        <>
          Are you sure you want to delete <strong>"{truncatedName}"</strong>?
          All Projects within this Workspace will be permanently deleted. This
          cannot be undone.
        </>
      );
    },
    /** Confirm button label for deleting a workspace */
    deleteWorkspaceConfirmButton: 'Delete Workspace',
    /** Toast label shown while deleting a workspace */
    deleteWorkspaceLoading: 'Deleting Workspace…',
    /** Dialog title for workspace deletion */
    deleteWorkspaceTitle: 'Delete Workspace?',
    /** Error message when deletion fails for a specific workspace */
    errorMessage: (name: string) => {
      const truncatedName = truncateName(name);

      return `Failed to delete the Workspace "${truncatedName}"`;
    },
    /** Generic error message for workspace deletion failures */
    genericErrorMessage: 'Failed to delete the Workspace',
    /** Success message when a workspace is deleted */
    successMessage: (name: string) => {
      const truncatedName = truncateName(name, 15);

      return `"${truncatedName}" Workspace was deleted`;
    },
  },
  { truncateName }
);
